import { styled, alpha } from '@mui/material/styles';
import { Box, Typography, Button, TextField, Select } from '@mui/material';
import { primaryColor } from '../../../../utils/theme';

export const PageWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: '#F8F9FA',
  minHeight: '100vh',
}));

export const HeaderSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

export const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 600,
  color: theme.palette.custom.sidebarText,
  marginBottom: theme.spacing(0.5),
}));

export const FormContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: '12px',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
  overflow: 'hidden',
}));

export const SectionHeader = styled(Box)(({ theme }) => ({
  backgroundColor: '#5E8E7E',
  color: theme.palette.white.main,
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  fontSize: '1rem',
}));

export const SectionContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  display: 'grid',
  gridTemplateColumns: 'repeat(12, 1fr)',
  gap: theme.spacing(3),
}));

export const FormGroup = styled(Box)<{ gridColumn?: string }>(({ gridColumn }) => ({
  gridColumn: gridColumn || 'span 12',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
}));

export const Label = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: theme.palette.grey[700],
}));

export const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#fff',
    '& fieldset': {
      borderColor: theme.palette.grey[300],
    },
    '&:hover fieldset': {
      borderColor: theme.palette.grey[400],
    },
    '&.Mui-focused fieldset': {
      borderColor: primaryColor,
    },
  },
  '& .MuiInputBase-input': {
    // padding: '14px',
  }
}));

export const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: '8px',
  backgroundColor: '#fff',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.grey[300],
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.grey[400],
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: primaryColor,
  },
  '& .MuiSelect-select': {
    minHeight: 'auto',
  }
}));

export const FeaturesHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2, 3),
  borderBottom: `1px solid ${theme.palette.grey[200]}`,
  gap: theme.spacing(2),
}));

export const FeatureSearch = styled(TextField)(({ theme }) => ({
  width: '400px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    height: '32px',
    backgroundColor: '#fff',
    fontSize: '0.875rem',
  }
}));

export const FeatureList = styled(Box)(({ theme }) => ({
  maxHeight: '400px',
  overflowY: 'auto',
  padding: theme.spacing(1, 0),
}));

export const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1.5fr 2fr 1fr 60px',
  alignItems: 'center',
  padding: theme.spacing(1.5, 3),
  borderBottom: `1px solid ${theme.palette.grey[100]}`,
  '&:last-child': {
    borderBottom: 'none',
  },
}));

export const TypeBadge = styled(Box)<{ type: string }>(({ theme, type }) => ({
  display: 'inline-block',
  padding: '4px 12px',
  borderRadius: '4px',
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  backgroundColor: (() => {
    const t = (type || '').toLowerCase().replace(/[_\s]/g, '');
    if (t === 'superadmin') return alpha('#1976D2', 0.1);
    if (t === 'facility') return alpha('#ED6C02', 0.1);
    return alpha('#2E7D32', 0.1);
  })(),
  color: (() => {
    const t = (type || '').toLowerCase().replace(/[_\s]/g, '');
    if (t === 'superadmin') return '#1976D2';
    if (t === 'facility') return '#ED6C02';
    return '#2E7D32';
  })(),
  width: 'fit-content',
}));

export const ActionButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(2),
  padding: theme.spacing(3),
  borderTop: `1px solid ${theme.palette.grey[200]}`,
}));

export const CancelButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  padding: '8px 40px',
  borderRadius: '8px',
  border: `1px solid ${primaryColor}`,
  color: primaryColor,
  fontWeight: 600,
  '&:hover': {
    backgroundColor: alpha(primaryColor, 0.05),
    borderColor: primaryColor,
  }
}));

export const CreateButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  padding: '8px 40px',
  borderRadius: '8px',
  backgroundColor: primaryColor,
  color: '#fff',
  fontWeight: 600,
  '&:hover': {
    backgroundColor: alpha(primaryColor, 0.9),
  },
  '&.Mui-disabled': {
    backgroundColor: alpha(primaryColor, 0.5),
    color: '#fff',
  }
}));

export const EnabledCountBadge = styled(Box)(({ theme }) => ({
  backgroundColor: '#E9EDF7',
  color: '#1A1C1E',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  padding: '0 20px',
  borderRadius: '8px',
  fontSize: '0.85rem',
  fontWeight: 500,
}));
